import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { AuthApi } from "@/lib/apiClient";
import { toast } from "sonner";
import { KeyRound, Loader2, ShieldAlert } from "lucide-react";

export default function ChangePasswordDialog({ open, onOpenChange, forced = false, onChanged }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  const reset = () => { setCurrent(""); setNext(""); setConfirm(""); };

  const submit = async () => {
    if (!current || !next) { toast.error("Compila tutti i campi"); return; }
    if (next.length < 6) { toast.error("La nuova password deve avere almeno 6 caratteri"); return; }
    if (next !== confirm) { toast.error("Le due nuove password non coincidono"); return; }
    if (next === current) { toast.error("La nuova password deve essere diversa da quella attuale"); return; }
    setSaving(true);
    try {
      await AuthApi.changePassword(current, next);
      toast.success("Password aggiornata");
      reset();
      onChanged && onChanged();
      if (!forced) onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore cambio password");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (forced) return; if (!o) reset(); onOpenChange(o); }}>
      <DialogContent
        className={`bg-white max-w-sm ${forced ? "[&>button]:hidden" : ""}`}
        data-testid="change-password-dialog"
        onEscapeKeyDown={(e) => forced && e.preventDefault()}
        onPointerDownOutside={(e) => forced && e.preventDefault()}
        onInteractOutside={(e) => forced && e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            {forced ? <ShieldAlert className="h-5 w-5 text-primary" /> : <KeyRound className="h-5 w-5 text-primary" />}
            {forced ? "Primo accesso — scegli la tua password" : "Cambia password"}
          </DialogTitle>
          <DialogDescription>
            {forced
              ? "Per motivi di sicurezza devi impostare una password personale prima di continuare."
              : "Aggiorna la tua password personale."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-muted-foreground">{forced ? "Password ricevuta" : "Password attuale"}</label>
            <Input type="password" value={current} onChange={(e) => setCurrent(e.target.value)} data-testid="current-password-input" placeholder={forced ? "Password ricevuta dall'admin" : "Password attuale"} className="h-9" autoFocus />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-muted-foreground">Nuova password</label>
            <Input type="password" value={next} onChange={(e) => setNext(e.target.value)} data-testid="new-password-input" placeholder="Almeno 6 caratteri" className="h-9" />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-muted-foreground">Conferma nuova password</label>
            <Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="confirm-password-input" placeholder="Ripeti la nuova password" className="h-9" />
          </div>
          <Button className="w-full" disabled={saving} data-testid="change-password-submit" onClick={submit}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (forced ? "Imposta password e continua" : "Aggiorna password")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
